import {Component, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {CalendarFormat} from '@domain/calendar/schedule/custom-settings/calendar-format.enum';
import {NonWorkingDay} from '@domain/calendar/schedule/custom-settings/non-working-day';
import {ScheduleCustomSettings} from '@domain/calendar/schedule/custom-settings/schedule-custom-settings';
import {StartOfDay} from '@domain/calendar/schedule/custom-settings/start-of-day.enum';
import {StartOfWeek} from '@domain/calendar/schedule/custom-settings/start-of-week.enum';
import {HttpSchedulesCustomSettingsService} from '@infrastructure/http/calendar/schedule/http-schedules-custom-settings.service';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  CustomCellEditorComponent,
  CustomCellEditorParams,
  EnumUtils,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  SuffixIconCellRendererComponent,
  SuffixIconCellRendererParams,
  TabsMenuItem,
  ToastService,
  TranslateService,
  UuidUtils
} from '@vdw/angular-component-library';
import {ColDef, EditableCallbackParams, GridApi, GridReadyEvent, ITooltipParams, RowNode} from 'ag-grid-community';
import {DateTime} from 'luxon';
import {takeUntil} from 'rxjs';
import {NonWorkingDayForm, ScheduleCustomSettingsForm} from './schedule-custom-settings.form';

@Component({
  selector: 'app-schedule-custom-settings',
  templateUrl: './schedule-custom-settings.component.html',
  styleUrls: ['./schedule-custom-settings.component.scss']
})
export class ScheduleCustomSettingsComponent extends BaseComponent implements OnInit {
  public scheduleForm: ScheduleCustomSettingsForm;
  public nonWorkingDaysConfig: CrudOverviewDataConfig<NonWorkingDay>;
  public readonly calenderFormats = EnumUtils.getEnumValues(CalendarFormat);
  public readonly startOfWeekFormats = EnumUtils.getEnumValues(StartOfWeek);
  public readonly startOfDayFormats = EnumUtils.getEnumValues(StartOfDay);

  private gridApi: GridApi;
  private readonly NEXT_DAY = DateTime.now().plus({days: 1}).startOf('day');

  protected SCHEDULE_TRANSLATION_KEY = 'CALENDAR.SCHEDULE.SCHEDULE';
  protected generalTab: TabsMenuItem = {
    value: 'generalTab',
    translationKey: 'GENERAL.GENERAL'
  };

  private get nonWorkingDays(): FormArray<NonWorkingDayForm> {
    return this.scheduleForm?.controls?.nonWorkingDays;
  }

  public constructor(
    private readonly schedulesCustomSettings: HttpSchedulesCustomSettingsService,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<NonWorkingDay>,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly dialogRef: MatDialogRef<ScheduleCustomSettingsComponent>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setFormFields();
    this.setNonWorkingDaysConfig();
    this.getCustomSettings();
  }

  public save(): void {
    if (this.scheduleForm.valid) {
      const nonWorkingDays = this.nonWorkingDays.controls
        .filter((control: NonWorkingDayForm) => control.value.name != null || control.value.start != null || control.value.end != null)
        .map((control: NonWorkingDayForm) => new NonWorkingDay(AssertionUtils.isNumber(control.value.id) ? control.value.id : null, control.value.name, control.value.start, control.value.end));
      const customSettings = new ScheduleCustomSettings(this.scheduleForm.value.calendarFormat, this.scheduleForm.value.startOfWeek, this.scheduleForm.value.startOfDay, nonWorkingDays);

      this.schedulesCustomSettings
        .save(customSettings)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe(() => {
          this.closeDialog();
          this.toastService.showInfo({
            tapToDismiss: false,
            timeOut: 2000,
            message: this.translate.instant('GENERAL.ACTIONS.UPDATED_OBJECT_WITHOUT_NAME', {object: 'GENERAL.CUSTOM_SETTINGS.CUSTOM_SETTINGS'})
          });
        });
    }
  }

  public addNonWorkingDay(nonWorkingDay: NonWorkingDay = null): void {
    const nonWorkingDayForm = new FormGroup({
      id: new FormControl(nonWorkingDay?.id ?? UuidUtils.generateV4Uuid()),
      name: new FormControl(nonWorkingDay?.name),
      start: new FormControl(nonWorkingDay?.start),
      end: new FormControl(nonWorkingDay?.end)
    }) as NonWorkingDayForm;

    this.setValidations(nonWorkingDayForm);

    this.nonWorkingDays.push(nonWorkingDayForm);
    this.gridApi?.applyTransaction({add: [nonWorkingDayForm.value]});
    this.gridApi?.refreshCells();
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  private setFormFields(): void {
    this.scheduleForm = this.formBuilder.group({
      calendarFormat: [CalendarFormat.STANDARD_GREGORIAN],
      startOfWeek: [StartOfWeek.MONDAY],
      startOfDay: [StartOfDay.FIRST_SHIFT_START],
      nonWorkingDays: new FormArray([])
    });
  }

  private getCustomSettings(): void {
    this.schedulesCustomSettings
      .get()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((customSettings: ScheduleCustomSettings) => {
        this.scheduleForm.reset({
          calendarFormat: customSettings.calendarFormat,
          startOfWeek: customSettings.startOfWeek,
          startOfDay: customSettings.startOfDay,
          nonWorkingDays: []
        });

        if (customSettings.nonWorkingDays.length > 0) {
          customSettings.nonWorkingDays.forEach((nonWorkingDay: NonWorkingDay) => this.addNonWorkingDay(nonWorkingDay));
          this.gridApi.setGridOption('rowData', this.nonWorkingDays?.value as NonWorkingDay[]);
        } else {
          this.addNonWorkingDay();
          this.gridApi.setGridOption('rowData', this.nonWorkingDays?.value as NonWorkingDay[]);
        }
      });
  }

  private setValidations(nonWorkingDayForm: NonWorkingDayForm): void {
    nonWorkingDayForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      if (!AssertionUtils.isNullOrUndefined(this.gridApi)) {
        setTimeout(() => {
          const controls = [nonWorkingDayForm.controls.name, nonWorkingDayForm.controls.start, nonWorkingDayForm.controls.end];
          const hasAnyValue = controls.some((control: any) => !!control.value);

          controls.forEach((control: any) => {
            if (hasAnyValue) {
              control.setValidators(Validators.required);
            } else {
              control.clearValidators();
              control.setErrors(null);
            }
            control.updateValueAndValidity({emitEvent: false});
          });

          const data = this.gridApi.getRowNode(nonWorkingDayForm.value.id.toString()).data;
          data.errors = {name: nonWorkingDayForm.controls.name.invalid, start: nonWorkingDayForm.controls.start.invalid, end: nonWorkingDayForm.controls.end.invalid};

          this.gridApi.refreshCells();
        });
      }
    });
  }

  private setNonWorkingDaysConfig(): void {
    this.nonWorkingDaysConfig = this.builderFactoryService
      .getBuilder()
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          withDelete: (grid: GridButtonClickedEvent) => this.deleteNonWorkingDay(grid),
          canBeDeleted: (data: NonWorkingDay) => this.canDeleted(data),
          allowDeleteMultiple: true
        } as CrudActions)
      })
      .withGridOptions(this.getNonWorkingDayColumnDefs(), GridIdentifier.NON_WORKING_DAY, this.nonWorkingDays?.value as NonWorkingDay[], (builder: GridOptionsBuilder) =>
        builder
          .withOnGridReady((event: GridReadyEvent) => (this.gridApi = event.api))
          .withRowSelection(true)
          .withOnCellEditingStopped()
          .withIsRowSelectable((rowNode: RowNode) => this.canDeleted(rowNode.data), false)
          .build()
      )
      .withoutSelectedToggle()
      .build();
  }

  private getNonWorkingDayColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.NAME')
        .withColIdAndField('name', true)
        .withCellRenderer(SuffixIconCellRendererComponent, {placeholder: this.translate.instant('CALENDAR.SCHEDULE.CUSTOM_SETTINGS.HOLIDAY')})
        .withEditable(
          (params: EditableCallbackParams) => this.isEditable(params.data.start) || this.isEditable(params.data.end),
          CustomCellEditorComponent,
          (params: CustomCellEditorParams) => ({
            placeholder: this.translate.instant('CALENDAR.SCHEDULE.CUSTOM_SETTINGS.HOLIDAY'),
            formControl: this.nonWorkingDays.at(params.node.rowIndex).controls.name
          })
        )
        .withTooltipValueGetter((params: ITooltipParams) => (AssertionUtils.isNullOrUndefined(params.value) ? null : `${params.value}`))
        .withOnCellClicked()
        .withSuppressHeaderMenuButton()
        .withoutFilter()
        .withFlex()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.START_DATE')
        .withColIdAndField('start')
        .withCellRenderer(SuffixIconCellRendererComponent, {icon: 'solid-calendar-today', objectKey: 'ANGULAR_COMPONENT_LIBRARY.CUSTOM_CELL_EDITOR.DATE'} as SuffixIconCellRendererParams)
        .withEditable(
          (params: EditableCallbackParams) => this.isEditable(params.data.start),
          CustomCellEditorComponent,
          (params: CustomCellEditorParams) => ({
            min: this.NEXT_DAY,
            max: params.data.end,
            number: false,
            date: true,
            formControl: this.nonWorkingDays.at(params.node.rowIndex).controls.start
          }),
          true
        )
        .withOnCellClicked()
        .withSuppressHeaderMenuButton()
        .withoutFilter()
        .withFlex()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.END_DATE')
        .withColIdAndField('end')
        .withCellRenderer(SuffixIconCellRendererComponent, {icon: 'solid-calendar-today', objectKey: 'ANGULAR_COMPONENT_LIBRARY.CUSTOM_CELL_EDITOR.DATE'} as SuffixIconCellRendererParams)
        .withEditable(
          (params: EditableCallbackParams) => this.isEditable(params.data.end),
          CustomCellEditorComponent,
          (params: CustomCellEditorParams) => ({
            min: params.data.start ?? this.NEXT_DAY,
            number: false,
            date: true,
            formControl: this.nonWorkingDays.at(params.node.rowIndex).controls.end
          }),
          true
        )
        .withOnCellClicked()
        .withSuppressHeaderMenuButton()
        .withoutFilter()
        .withFlex()
        .build()
    ];
  }

  private deleteNonWorkingDay({gridApi, rowNode}: GridButtonClickedEvent): void {
    const rowsToRemove = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows() : [rowNode.data];
    rowsToRemove.forEach((rowToRemove: NonWorkingDay) =>
      this.nonWorkingDays.removeAt(this.nonWorkingDays.controls.findIndex((form: NonWorkingDayForm) => form === this.getNonWorkingDayFormById(rowToRemove.id)))
    );
    this.gridApi.applyTransaction({remove: rowsToRemove});
    this.gridApi.refreshCells();
  }

  private getNonWorkingDayFormById(id: number | string): NonWorkingDayForm {
    return this.nonWorkingDays.at(this.nonWorkingDays.value.findIndex((data: NonWorkingDay) => data.id.toString() === id.toString()));
  }

  private isEditable(date: DateTime): boolean {
    if (AssertionUtils.isNullOrUndefined(date)) {
      return true;
    }

    return date.startOf('day') >= this.NEXT_DAY;
  }

  private canDeleted(data: NonWorkingDay): boolean {
    return this.gridApi.getDisplayedRowCount() > 1 && (this.isEditable(data.start) || this.isEditable(data.end));
  }
}
